import React from 'react';
import { Chip, Tooltip } from '@mui/material';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import PersonIcon from '@mui/icons-material/Person';
import HandshakeIcon from '@mui/icons-material/Handshake';
import { StepKind } from '../../types/generated/graphql';
import { colors } from '../../theme/colors';

interface AutomationBadgeProps {
  kind: StepKind;
  hasActions?: boolean;
  hasSkills?: boolean;
  size?: 'small' | 'medium';
  showLabel?: boolean;
}

/**
 * Badge showing the automation level of a step
 * 
 * - AUTOMATION: Robot icon, green - fully automated
 * - HUMAN: Person icon, blue - requires human
 * - MIXED: Handshake icon, purple - human + automation
 */
export const AutomationBadge: React.FC<AutomationBadgeProps> = ({
  kind,
  hasActions = false,
  hasSkills = false,
  size = 'small',
  showLabel = false,
}) => {
  const getConfig = () => {
    switch (kind) {
      case StepKind.Automation:
        return {
          icon: <SmartToyIcon sx={{ fontSize: size === 'small' ? 14 : 18 }} />,
          label: 'Automated',
          tooltip: hasActions || hasSkills 
            ? 'This step will execute automatically'
            : 'Automation step (no actions configured)',
          bgColor: hasActions || hasSkills ? `${colors.green[600]}15` : `${colors.gray[400]}15`,
          color: hasActions || hasSkills ? colors.green[600] : colors.gray[500],
          borderColor: hasActions || hasSkills ? `${colors.green[600]}30` : `${colors.gray[400]}30`,
        };
      case StepKind.Human:
        return {
          icon: <PersonIcon sx={{ fontSize: size === 'small' ? 14 : 18 }} />,
          label: 'Manual',
          tooltip: 'This step requires human action',
          bgColor: `${colors.blue[600]}15`,
          color: colors.blue[600],
          borderColor: `${colors.blue[600]}30`,
        };
      case StepKind.Mixed:
        return {
          icon: <HandshakeIcon sx={{ fontSize: size === 'small' ? 14 : 18 }} />,
          label: 'Assisted',
          tooltip: 'This step combines human action with automation',
          bgColor: `${colors.purple[600]}15`,
          color: colors.purple[600],
          borderColor: `${colors.purple[600]}30`,
        };
      default:
        return {
          icon: <PersonIcon sx={{ fontSize: size === 'small' ? 14 : 18 }} />,
          label: 'Unknown',
          tooltip: 'Step type unknown',
          bgColor: `${colors.gray[400]}15`,
          color: colors.gray[500],
          borderColor: `${colors.gray[400]}30`,
        };
    }
  };

  const config = getConfig();

  return (
    <Tooltip title={config.tooltip} arrow placement="top">
      <Chip
        icon={config.icon}
        label={showLabel ? config.label : undefined}
        size={size}
        sx={{
          bgcolor: config.bgColor,
          color: config.color,
          border: `1px solid ${config.borderColor}`,
          fontWeight: 500,
          fontSize: size === 'small' ? '0.65rem' : '0.75rem',
          height: size === 'small' ? 20 : 24,
          '& .MuiChip-icon': {
            color: config.color,
            marginLeft: showLabel ? '4px' : '8px',
            marginRight: showLabel ? '-4px' : '8px',
          },
          '& .MuiChip-label': {
            px: showLabel ? 1 : 0,
          },
          // If no label, make it a small icon-only chip
          ...(showLabel ? {} : {
            minWidth: size === 'small' ? 28 : 32,
            '& .MuiChip-label': {
              display: 'none',
            },
          }),
        }}
      />
    </Tooltip>
  );
};

export default AutomationBadge;

